import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, CheckCircle, ClipboardList, BarChart3, CircleDot, CheckSquare, Type, ArrowLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicSurveyPage() {
  const { surveyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [responsesCount, setResponsesCount] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => { fetchSurvey(); }, [surveyId]);

  const fetchSurvey = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/surveys/${surveyId}`);
      setSurvey(res.data);
      setResponsesCount(res.data.responses_count || 0);
      // Initialize answers
      const init = {};
      (res.data.questions || []).forEach(q => {
        if (q.type === 'text') init[q.id] = '';
        else if (q.type === 'multiple_quantity') init[q.id] = { selected: [], quantities: {} };
        else init[q.id] = [];
      });
      setAnswers(init);
    } catch {
      setError('Umfrage nicht gefunden oder nicht mehr aktiv.');
    } finally { setLoading(false); }
  };

  const toggleOption = (qId, optIdx, type) => {
    setAnswers(prev => {
      if (type === 'single') {
        return { ...prev, [qId]: [optIdx] };
      }
      if (type === 'multiple_quantity') {
        const current = prev[qId] || { selected: [], quantities: {} };
        const selected = current.selected || [];
        const quantities = { ...current.quantities };
        if (selected.includes(optIdx)) {
          delete quantities[optIdx];
          return { ...prev, [qId]: { selected: selected.filter(i => i !== optIdx), quantities } };
        } else {
          if (!quantities[optIdx]) quantities[optIdx] = 1;
          return { ...prev, [qId]: { selected: [...selected, optIdx], quantities } };
        }
      }
      // multiple
      const current = prev[qId] || [];
      return {
        ...prev,
        [qId]: current.includes(optIdx)
          ? current.filter(i => i !== optIdx)
          : [...current, optIdx],
      };
    });
  };

  const setQuantity = (qId, optIdx, qty) => {
    setAnswers(prev => {
      const current = prev[qId] || { selected: [], quantities: {} };
      return {
        ...prev,
        [qId]: {
          ...current,
          quantities: { ...current.quantities, [optIdx]: parseInt(qty) || 0 }
        }
      };
    });
  };

  const setText = (qId, text) => {
    setAnswers(prev => ({ ...prev, [qId]: text }));
  };

  const submit = async () => {
    // Validate: at least answer one question
    const hasAnswer = survey.questions.some(q => {
      const a = answers[q.id];
      if (q.type === 'text') return a?.trim();
      if (q.type === 'multiple_quantity') return a?.selected?.length > 0;
      return a?.length > 0;
    });
    if (!hasAnswer) {
      toast.error('Bitte mindestens eine Frage beantworten');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        answers: survey.questions.map(q => {
          const a = answers[q.id];
          if (q.type === 'multiple_quantity') {
            const selected = a?.selected || [];
            const quantities = selected.map(idx => ({
              option_index: idx,
              quantity: a?.quantities?.[idx] || 0
            }));
            return {
              question_id: q.id,
              selected_options: selected,
              quantities,
              text: '',
            };
          }
          return {
            question_id: q.id,
            selected_options: q.type !== 'text' ? (a || []) : [],
            text: q.type === 'text' ? (a || '') : '',
          };
        }),
      };
      const res = await axios.post(`${API_URL}/api/public/surveys/${surveyId}/respond`, payload);
      setResults(res.data.results);
      setResponsesCount(res.data.responses_count);
      setSubmitted(true);
    } catch {
      toast.error('Fehler beim Absenden');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header bar */}
      <div className="bg-primary text-white py-3 px-4 flex items-center justify-between">
        <button
          onClick={() => { window.history.length > 1 ? window.history.back() : window.location.href = '/home'; }}
          className="flex items-center gap-1 text-white/80 hover:text-white transition-colors"
          data-testid="survey-back-btn"
        >
          <ArrowLeft size={18} />
          <span className="text-sm">Zurück</span>
        </button>
        <span className="text-sm font-medium tracking-wider uppercase">Swiss Historic Racing Team</span>
        <div className="w-16" />
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Survey header */}
        <div className="mb-8">
          <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide" data-testid="survey-title">
            {survey.title}
          </h1>
          {survey.description && (
            <p className="text-muted-foreground mt-2">{survey.description}</p>
          )}
          {!submitted && (
            <p className="text-xs text-muted-foreground mt-2">
              Anonym &middot; {responsesCount} bisherige Teilnahmen
            </p>
          )}
        </div>

        {/* ====== FORM ====== */}
        {!submitted && survey.status === 'active' && (
          <>
            <div className="space-y-6">
              {survey.questions.map((q, qIdx) => (
                <div key={q.id} className="bg-card border border-border p-5" data-testid={`public-question-${qIdx}`}>
                  <h3 className="font-semibold mb-3">
                    <span className="text-primary mr-2">{qIdx + 1}.</span>
                    {q.text}
                  </h3>

                  {q.image_url && (
                    <img src={q.image_url} alt="" className="max-h-64 w-auto mb-4 border border-border" />
                  )}

                  {q.type === 'single' && (
                    <div className="space-y-2">
                      {q.options.map((opt, oIdx) => (
                        <label
                          key={oIdx}
                          className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                            (answers[q.id] || []).includes(oIdx)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/30'
                          }`}
                          data-testid={`option-${qIdx}-${oIdx}`}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            (answers[q.id] || []).includes(oIdx) ? 'border-primary' : 'border-muted-foreground'
                          }`}>
                            {(answers[q.id] || []).includes(oIdx) && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                          </div>
                          <span className="text-sm">{opt}</span>
                          <input type="radio" name={q.id} className="sr-only" onChange={() => toggleOption(q.id, oIdx, 'single')} />
                        </label>
                      ))}
                    </div>
                  )}

                  {q.type === 'multiple' && (
                    <div className="space-y-2">
                      {q.options.map((opt, oIdx) => (
                        <label
                          key={oIdx}
                          className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                            (answers[q.id] || []).includes(oIdx)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/30'
                          }`}
                          data-testid={`option-${qIdx}-${oIdx}`}
                        >
                          <div className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${
                            (answers[q.id] || []).includes(oIdx) ? 'border-primary bg-primary' : 'border-muted-foreground'
                          }`}>
                            {(answers[q.id] || []).includes(oIdx) && (
                              <svg className="w-3 h-3 text-white" viewBox="0 0 12 12"><path d="M10 3L4.5 8.5 2 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            )}
                          </div>
                          <span className="text-sm">{opt}</span>
                          <input type="checkbox" className="sr-only" onChange={() => toggleOption(q.id, oIdx, 'multiple')} />
                        </label>
                      ))}
                    </div>
                  )}

                  {q.type === 'multiple_quantity' && (
                    <div className="space-y-2">
                      {q.options.map((opt, oIdx) => {
                        const sel = (answers[q.id]?.selected || []).includes(oIdx);
                        return (
                          <div
                            key={oIdx}
                            className={`flex items-center gap-3 p-3 border transition-colors ${
                              sel ? 'border-primary bg-primary/5' : 'border-border'
                            }`}
                            data-testid={`option-${qIdx}-${oIdx}`}
                          >
                            <label className="flex items-center gap-3 flex-1 cursor-pointer min-w-0">
                              <div className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${
                                sel ? 'border-primary bg-primary' : 'border-muted-foreground'
                              }`}>
                                {sel && (
                                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12"><path d="M10 3L4.5 8.5 2 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                )}
                              </div>
                              <span className="text-sm flex-1">{opt}</span>
                              <input type="checkbox" className="sr-only" checked={sel} onChange={() => toggleOption(q.id, oIdx, 'multiple_quantity')} />
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={answers[q.id]?.quantities?.[oIdx] ?? 0}
                              onChange={(e) => setQuantity(q.id, oIdx, e.target.value)}
                              className="w-16 h-9 text-center bg-background border border-input text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                              data-testid={`quantity-${qIdx}-${oIdx}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {q.type === 'text' && (
                    <textarea
                      value={answers[q.id] || ''}
                      onChange={(e) => setText(q.id, e.target.value)}
                      placeholder="Deine Antwort..."
                      className="w-full bg-background border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[80px] resize-y"
                      data-testid={`text-answer-${qIdx}`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Button onClick={submit} disabled={submitting} className="w-full rounded-none uppercase tracking-wider py-3" data-testid="submit-survey-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Absenden
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2">
                Deine Antworten sind anonym und werden nicht mit deinem Namen verknüpft.
              </p>
            </div>
          </>
        )}

        {survey.status === 'closed' && !submitted && (
          <div className="text-center py-12 bg-card border border-border">
            <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">Diese Umfrage ist geschlossen.</p>
          </div>
        )}

        {/* ====== RESULTS ====== */}
        {submitted && results && (
          <div>
            <div className="bg-green-500/10 border border-green-500/30 p-4 mb-6 flex items-center gap-3" data-testid="survey-success">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <p className="font-semibold text-green-700">Vielen Dank für deine Teilnahme!</p>
                <p className="text-sm text-muted-foreground">{responsesCount} Teilnahmen insgesamt</p>
              </div>
            </div>

            <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wide flex items-center gap-2 mb-4">
              <BarChart3 className="text-primary" /> Bisherige Ergebnisse
            </h2>

            <div className="space-y-6">
              {survey.questions.map((q, qIdx) => {
                const result = results[q.id];
                if (!result) return null;
                return (
                  <div key={q.id} className="bg-card border border-border p-5" data-testid={`result-${qIdx}`}>
                    <h3 className="font-semibold mb-3">
                      <span className="text-primary mr-2">{qIdx + 1}.</span>
                      {q.text}
                    </h3>

                    {q.image_url && <img src={q.image_url} alt="" className="max-h-48 mb-4 border border-border" />}

                    {(q.type === 'single' || q.type === 'multiple') && (
                      <div className="space-y-2">
                        {q.options.map((opt, oIdx) => {
                          const count = result.option_counts[oIdx] || 0;
                          const pct = result.total > 0 ? Math.round((count / result.total) * 100) : 0;
                          const isMyChoice = (answers[q.id] || []).includes(oIdx);
                          return (
                            <div key={oIdx}>
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className={isMyChoice ? 'font-semibold' : ''}>{opt}{isMyChoice && ' (deine Wahl)'}</span>
                                <span className="text-muted-foreground">{count} ({pct}%)</span>
                              </div>
                              <div className="w-full h-6 bg-muted/50 overflow-hidden">
                                <div className={`h-full transition-all duration-700 ${isMyChoice ? 'bg-primary' : 'bg-primary/60'}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {q.type === 'text' && (
                      <p className="text-sm text-muted-foreground">
                        {result.texts.length} Freitext-Antworten erhalten
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-border mt-12 py-8 text-center text-xs text-muted-foreground">
        <div className="leading-relaxed mb-3">
          <p className="font-bold text-foreground">S.H.R.T.</p>
          <p>Swiss Historic Racing Team</p>
          <p className="text-[10px]">Verein gem&auml;ss Art. 60 ff. ZGB</p>
          <p className="mt-2">Kuno Sch&auml;r &middot; St. Niklausstrasse 1 &middot; CH-4502 Solothurn</p>
          <p>Tel. <a href="tel:+41794153031" className="hover:text-foreground">+41 79 415 30 31</a> &middot; <a href="mailto:info@kunoschaer.ch" className="hover:text-foreground">info@kunoschaer.ch</a></p>
        </div>
        <div className="flex justify-center gap-3 mb-2">
          <Link to="/datenschutz" className="hover:text-foreground">Datenschutz</Link>
          <span>|</span>
          <Link to="/agb" className="hover:text-foreground">AGB</Link>
          <span>|</span>
          <Link to="/impressum" className="hover:text-foreground">Impressum</Link>
        </div>
        &copy; {new Date().getFullYear()} Swiss Historic Racing Team
      </div>
    </div>
  );
}
